import { fromJS, OrderedMap } from 'immutable';
import { useCallback, useMemo, useRef } from 'react';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import MissingField from 'customer-data-filters-ui/filterQueryFormat/MissingField';
import { STRING } from 'customer-data-objects/property/PropertyTypes';
import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { hasFieldOverrideV2 } from '../permissions/gates';
import { findAdapterFromAssetFamily } from '../fetcher/adapter/utils/findAdapterFromAssetFamily';
import { applyExternalOptionsOverrides, applyFieldDefinitionOverrides } from 'customer-data-filters-ui/utils/propertyDefinitionMutations';
import { disableLifecycleStageProperties } from '../utils/lifecycleStateVisibility';
import { Metrics } from '../metrics/Metrics';
import { Map as ImmutableMap } from 'immutable';
import { jsx as _jsx } from "react/jsx-runtime";
const missingFieldErrorMessage = /*#__PURE__*/_jsx(FormattedMessage, {
  message: "customerDataFiltersUI.customerDataFilters.MissingField.fetchFailed"
});
export const utils = {
  getMissingFieldErrorMessage: () => missingFieldErrorMessage,
  makeCacheKey: (assetFamily, fetcher, params = {}) => {
    if (fetcher.search) {
      return `${assetFamily}__${params.ids}__${params.query}__${params.hasMore}__${params.offset}`;
    }
    return `${assetFamily}__${params.ids}__${params.hasMore}__${params.offset}`;
  },
  mapIdsToNoAccessMissingFields: (assetFamily, ids = []) => {
    let type = STRING;
    if (assetFamily === DSAssetFamilies.WORKFLOW) {
      type = DSAssetFamilies.WORKFLOW;
    }
    return ids.reduce((acc, id) => {
      return acc.set(id, MissingField({
        name: `${id}`,
        userHasNoAccess: true,
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type 'PropertyT... Remove this comment to see the full error message
        type
      }));
    }, OrderedMap());
  }
};
export function useFetchData(referenceResolversByDSAssetFamily, setData, getHasFilterFamilyAccess, strategy, isUngated) {
  const fetcherStatusesMap = useRef({});
  const memoizedReferenceResolversByDSAssetFamily = useMemo(() => referenceResolversByDSAssetFamily,
  /*
  Something is going on in ProvideReferenceResolvers that is called in
  referenceResolverAdapter.configureComponent(DSFilters) in DSFilters.js
  for some reason `referenceResolversByDSAssetFamily` is different on each re-render
  so we need to memoize it to prevent filters re-render on each draft change.
  TODO: investigate this a bit more
  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const handleNoFilterFamilyAccess = useCallback((assetFamily, ids) => {
    const missingFields = utils.mapIdsToNoAccessMissingFields(assetFamily, ids);
    const newAssetFamilyData = ids ? missingFields : OrderedMap();
    setData(prevData => {
      const prevAssetFamilyData = prevData.get(assetFamily) || OrderedMap();
      return prevData.set(assetFamily, prevAssetFamilyData.merge(newAssetFamilyData));
    });
    return Promise.resolve(missingFields);
  }, [setData]);
  const handlerFetchComplete = useCallback((assetFamily, result, supportedAdapter) => {
    return Metrics.timer('handler_fetch_complete', {
      adapter: supportedAdapter.getPerformanceTrackingKey()
    }).time(() => {
      const {
        hasMore,
        offset
      } = result;
      const currentFetcherStatus = fetcherStatusesMap.current[assetFamily];
      const newFetcherStatus = {
        hasMore: hasMore || false,
        offset: offset || 0,
        prevOffset: currentFetcherStatus !== null && currentFetcherStatus !== void 0 && currentFetcherStatus.hasMore ? (currentFetcherStatus === null || currentFetcherStatus === void 0 ? void 0 : currentFetcherStatus.offset) || 0 : (currentFetcherStatus === null || currentFetcherStatus === void 0 ? void 0 : currentFetcherStatus.prevOffset) || 0
      };
      fetcherStatusesMap.current[assetFamily] = newFetcherStatus;
      let newAssetFamilyData = ImmutableMap();
      let newAssetFamilyDataWithOverrides;
      setData(prevData => {
        const prevAssetFamilyData = prevData.get(assetFamily) || OrderedMap();
        try {
          newAssetFamilyData = supportedAdapter.getFormattedData(assetFamily, result, prevAssetFamilyData);
        } catch (e) {
          Raven.captureException(new Error(`Error formatting data`), {
            extra: {
              originalError: e,
              assetFamily,
              result
            }
          });
        }
        newAssetFamilyData = applyExternalOptionsOverrides(assetFamily, newAssetFamilyData);
        newAssetFamilyData = disableLifecycleStageProperties(assetFamily, newAssetFamilyData);
        if (hasFieldOverrideV2(isUngated)) {
          newAssetFamilyDataWithOverrides = applyFieldDefinitionOverrides(assetFamily, newAssetFamilyData, strategy.getFieldDefinitions(assetFamily, isUngated));
        } else {
          newAssetFamilyDataWithOverrides = newAssetFamilyData;
        }
        const customFields = strategy.getCustomFieldsForAssetFamily(assetFamily);
        newAssetFamilyDataWithOverrides = newAssetFamilyDataWithOverrides.merge(customFields);
        return prevData.merge({
          [assetFamily]: newAssetFamilyDataWithOverrides,
          fetcherStatuses: fromJS(fetcherStatusesMap.current)
        });
      });
      return newAssetFamilyDataWithOverrides;
    });
  }, [setData, strategy, isUngated]);
  const fetchData = useCallback(args => {
    const {
      variables: {
        assetFamily,
        ids,
        query
      }
    } = args;
    const fetcherStatus = fetcherStatusesMap.current[assetFamily];
    const hasMore = fetcherStatus ? fetcherStatus.hasMore : false;
    let offset = 0;
    if (fetcherStatus) {
      if (hasMore) {
        offset = fetcherStatus.offset;
      } else {
        offset = fetcherStatus.prevOffset;
      }
    }
    const fetchVariables = {
      getMissingFieldErrorMessage: utils.getMissingFieldErrorMessage,
      hasMore,
      ids,
      offset,
      query
    };
    const fetcherProps = {
      referenceResolversByDSAssetFamily: memoizedReferenceResolversByDSAssetFamily
    };
    const supportedAdapter = findAdapterFromAssetFamily(assetFamily);
    if (!supportedAdapter) {
      return Promise.resolve(undefined);
    }
    if (!getHasFilterFamilyAccess(assetFamily)) {
      return handleNoFilterFamilyAccess(assetFamily, ids);
    }
    const fetchPromise = supportedAdapter.getFetchedData(assetFamily, fetchVariables, fetcherProps);
    const requestPromise = fetchPromise.then(result => {
      // option to skip callback
      if (result.ignoreCallback) {
        return Promise.resolve(result);
      }
      return handlerFetchComplete(assetFamily, result, supportedAdapter);
    }).catch(error => {
      Raven.capturePageEvent('cdf-data-fetch-error', {
        extra: {
          errorMessage: error.message,
          error
        }
      });
    });
    return requestPromise;
  }, [handlerFetchComplete, memoizedReferenceResolversByDSAssetFamily, getHasFilterFamilyAccess, handleNoFilterFamilyAccess]);
  return fetchData;
}